import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2, Loader2, ShieldCheck, Upload, User, Calendar,
  Mail, MapPin, Phone, FileText, Camera, X, AlertTriangle, Home
} from 'lucide-react';
import PageMeta from '../PageMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Client-side image compression to keep payload small
const compressImage = (file, maxW = 1400) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.78));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const TenantOnboard = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    full_name: '',
    dob: '',
    email: '',
    permanent_address: '',
    emergency_contact_name: '',
    emergency_contact_number: '',
    aadhaar_number: '',
    aadhaar_front_image: '',
    aadhaar_back_image: '',
    selfie_image: '',
    agreement_accepted: false
  });

  const fetchMeta = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tenant-onboard/${token}`);
      setMeta(r.data);
      if (r.data.status === 'submitted' || r.data.status === 'verified') {
        setDone(true);
      }
      setForm((f) => ({
        ...f,
        full_name: r.data.tenant_name || ''
      }));
    } catch (e) {
      setError(e.response?.data?.detail || 'Link is invalid or expired.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.agreement_accepted) {
      alert('Please accept the rental agreement to continue.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/tenant-onboard/${token}/submit`, form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      alert(e.response?.data?.detail || 'Submit failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <Loader2 className="animate-spin text-emerald-600" size={36} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="onboard-error">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="text-red-600" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Link not valid</h1>
          <p className="text-gray-600">{error}</p>
          <p className="text-sm text-gray-400 mt-4">Ask your PG owner to send you a fresh link from WhatsApp.</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-green-100 p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="onboard-done">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5 animate-bounce">
            <CheckCircle2 className="text-emerald-600" size={44} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">All done, {form.full_name?.split(' ')[0] || 'friend'}! 🎉</h1>
          <p className="text-gray-600 mb-4">
            Your verification has been submitted to <span className="font-semibold">{meta?.property_name}</span>.
            We'll email you once the PG owner approves (usually under 24 hours).
          </p>
          <div className="bg-emerald-50 rounded-xl p-4 text-left text-sm text-emerald-900">
            <p><strong>Room:</strong> {meta?.room_number}</p>
            <p><strong>Move-in:</strong> {meta?.check_in_date}</p>
            {meta?.rent_amount ? <p><strong>Rent:</strong> ₹{Number(meta.rent_amount).toLocaleString('en-IN')}/month</p> : null}
          </div>
          <p className="text-[11px] text-gray-400 mt-6">Powered by RentKollect</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-emerald-50 pb-20" data-testid="tenant-onboard-page">
      <PageMeta
        title={meta?.property_name ? `Move-in verification — ${meta.property_name}` : 'Tenant move-in verification'}
        description="Secure KYC upload for your new PG / hostel room. Takes 2 minutes on your phone — no app install needed. Powered by RentKollect."
        noindex
      />
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-600 via-green-600 to-teal-600 text-white px-5 pt-10 pb-16 rounded-b-[28px] shadow-lg">
        <div className="max-w-md mx-auto">
          <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur px-2.5 py-1 rounded-full text-[11px] font-semibold mb-3">
            <ShieldCheck size={12} />
            RentKollect verification
          </div>
          <h1 className="text-2xl font-bold leading-tight mb-1">
            Hi {meta?.tenant_name?.split(' ')[0] || 'there'}! 👋
          </h1>
          <p className="text-emerald-100 text-sm leading-snug">
            <span className="font-semibold text-white">{meta?.property_name}</span>
            {meta?.property_address ? ` at ${meta.property_address}` : ''} needs a quick verification for your move-in.
            Takes 2 minutes.
          </p>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div className="bg-white/10 backdrop-blur rounded-lg p-2">
              <p className="text-[10px] text-emerald-200 uppercase tracking-wider">Room</p>
              <p className="font-bold">{meta?.room_number || '—'}</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-2">
              <p className="text-[10px] text-emerald-200 uppercase tracking-wider">Move-in</p>
              <p className="font-bold text-xs">{meta?.check_in_date || '—'}</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-2">
              <p className="text-[10px] text-emerald-200 uppercase tracking-wider">Rent</p>
              <p className="font-bold text-xs">
                {meta?.rent_amount ? `₹${Number(meta.rent_amount).toLocaleString('en-IN')}` : '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Step card */}
      <div className="max-w-md mx-auto px-5 -mt-10">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Step indicator */}
          <div className="flex border-b border-gray-100">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                data-testid={`onboard-step-indicator-${s}`}
                className={`flex-1 py-3 text-center text-xs font-semibold transition-colors ${
                  step === s ? 'text-emerald-600 border-b-2 border-emerald-600' :
                  step > s ? 'text-emerald-400' : 'text-gray-400'
                }`}
              >
                {s === 1 && 'Your details'}
                {s === 2 && 'Aadhaar'}
                {s === 3 && 'Agreement'}
              </div>
            ))}
          </div>

          <div className="p-5 space-y-4">
            {step === 1 && (
              <>
                <Field icon={User} label="Full name (as on Aadhaar)" required>
                  <input
                    data-testid="onboard-full-name"
                    type="text"
                    value={form.full_name}
                    onChange={(e) => update('full_name', e.target.value)}
                    placeholder="Rohit Sharma"
                    className="input"
                  />
                </Field>

                <Field icon={Calendar} label="Date of birth" required>
                  <input
                    data-testid="onboard-dob"
                    type="date"
                    value={form.dob}
                    onChange={(e) => update('dob', e.target.value)}
                    className="input"
                  />
                </Field>

                <Field icon={Mail} label="Email">
                  <input
                    data-testid="onboard-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder="you@example.com"
                    className="input"
                  />
                </Field>

                <Field icon={MapPin} label="Permanent address">
                  <textarea
                    data-testid="onboard-address"
                    rows={2}
                    value={form.permanent_address}
                    onChange={(e) => update('permanent_address', e.target.value)}
                    placeholder="Your hometown address"
                    className="input"
                  />
                </Field>

                <Field icon={User} label="Emergency contact name" required>
                  <input
                    data-testid="onboard-ec-name"
                    type="text"
                    value={form.emergency_contact_name}
                    onChange={(e) => update('emergency_contact_name', e.target.value)}
                    placeholder="Parent / sibling / friend"
                    className="input"
                  />
                </Field>

                <Field icon={Phone} label="Emergency contact mobile" required>
                  <input
                    data-testid="onboard-ec-phone"
                    type="tel"
                    value={form.emergency_contact_number}
                    onChange={(e) => update('emergency_contact_number', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit mobile"
                    className="input"
                  />
                </Field>

                <button
                  data-testid="onboard-step1-next"
                  onClick={() => {
                    if (!form.full_name || !form.dob || !form.emergency_contact_name || form.emergency_contact_number.length !== 10) {
                      alert('Please fill name, DOB and emergency contact.');
                      return;
                    }
                    setStep(2);
                  }}
                  className="w-full mt-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold py-3 rounded-xl shadow hover:shadow-lg transition-shadow"
                >
                  Continue →
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <Field icon={FileText} label="Aadhaar number" required>
                  <input
                    data-testid="onboard-aadhaar-number"
                    type="text"
                    inputMode="numeric"
                    value={form.aadhaar_number}
                    onChange={(e) => update('aadhaar_number', e.target.value.replace(/\D/g, '').slice(0, 12))}
                    placeholder="12-digit Aadhaar"
                    className="input font-mono"
                  />
                </Field>

                <ImageUpload
                  label="Aadhaar front photo"
                  value={form.aadhaar_front_image}
                  onChange={(v) => update('aadhaar_front_image', v)}
                  testId="onboard-aadhaar-front"
                />
                <ImageUpload
                  label="Aadhaar back photo"
                  value={form.aadhaar_back_image}
                  onChange={(v) => update('aadhaar_back_image', v)}
                  testId="onboard-aadhaar-back"
                />
                <ImageUpload
                  label="Selfie (hold Aadhaar next to face)"
                  value={form.selfie_image}
                  onChange={(v) => update('selfie_image', v)}
                  testId="onboard-selfie"
                  useFrontCamera
                />

                <div className="flex gap-2 pt-2">
                  <button
                    data-testid="onboard-step2-back"
                    onClick={() => setStep(1)}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-xl transition-colors"
                  >
                    Back
                  </button>
                  <button
                    data-testid="onboard-step2-next"
                    onClick={() => {
                      if (form.aadhaar_number.length !== 12 || !form.aadhaar_front_image || !form.aadhaar_back_image) {
                        alert('Please enter Aadhaar number and upload both Aadhaar photos.');
                        return;
                      }
                      setStep(3);
                    }}
                    className="flex-1 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold py-3 rounded-xl shadow hover:shadow-lg transition-shadow"
                  >
                    Continue →
                  </button>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900">
                  <h3 className="font-bold mb-2 flex items-center gap-1.5">
                    <FileText size={16} /> Rental Agreement — {meta?.property_name}
                  </h3>
                  <div className="space-y-1.5 text-[13px] leading-relaxed">
                    <p>I, <strong>{form.full_name || '___________'}</strong>, hereby agree to rent room <strong>{meta?.room_number}</strong> at <strong>{meta?.property_name}</strong>{meta?.property_address ? `, ${meta.property_address}` : ''} from <strong>{meta?.check_in_date}</strong>.</p>
                    <p>Monthly rent: <strong>₹{Number(meta?.rent_amount || 0).toLocaleString('en-IN')}</strong>. Security deposit: <strong>₹{Number(meta?.deposit_amount || 0).toLocaleString('en-IN')}</strong>.</p>
                    <p>I agree to: pay rent on the 1st of each month, give 1 month notice before leaving, follow house rules, and allow the owner to verify the documents I've provided.</p>
                    <p>I confirm the Aadhaar I uploaded belongs to me and is genuine.</p>
                  </div>
                </div>

                <label className="flex items-start gap-3 p-4 rounded-xl border-2 border-emerald-200 hover:bg-emerald-50 cursor-pointer" data-testid="onboard-agree-label">
                  <input
                    data-testid="onboard-agree-checkbox"
                    type="checkbox"
                    checked={form.agreement_accepted}
                    onChange={(e) => update('agreement_accepted', e.target.checked)}
                    className="mt-1 w-4 h-4 accent-emerald-600"
                  />
                  <span className="text-sm text-gray-800">
                    I have read and accept the rental agreement above. I understand my e-signature is legally valid under the IT Act 2000.
                  </span>
                </label>

                <div className="flex gap-2 pt-2">
                  <button
                    data-testid="onboard-step3-back"
                    onClick={() => setStep(2)}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-xl transition-colors"
                  >
                    Back
                  </button>
                  <button
                    data-testid="onboard-submit"
                    onClick={submit}
                    disabled={submitting || !form.agreement_accepted}
                    className="flex-1 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold py-3 rounded-xl shadow hover:shadow-lg transition-shadow disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
                    {submitting ? 'Submitting...' : 'Accept & submit'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6 flex items-center justify-center gap-1.5">
          <Home size={12} /> Powered by RentKollect · Your PG, managed from WhatsApp.
        </p>
      </div>

      <style>{`
        .input {
          width: 100%;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          padding: 0.625rem 0.875rem;
          font-size: 0.9rem;
          outline: none;
          transition: border-color .15s, box-shadow .15s;
        }
        .input:focus { border-color: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,.15); }
      `}</style>
    </div>
  );
};

const Field = ({ icon: Icon, label, required, children }) => (
  <div>
    <label className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
      {Icon && <Icon size={13} />} {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const ImageUpload = ({ label, value, onChange, testId, useFrontCamera }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);

  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const data = await compressImage(file);
      onChange(data);
    } catch (e) {
      alert('Could not read image, try another photo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
        <Camera size={13} /> {label} <span className="text-red-500">*</span>
      </label>
      {value ? (
        <div className="relative rounded-xl overflow-hidden border border-emerald-200" data-testid={`${testId}-preview`}>
          <img src={value} alt={label} className="w-full h-40 object-cover bg-gray-100" />
          <button
            data-testid={`${testId}-remove`}
            onClick={() => onChange('')}
            className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow"
            aria-label="Remove"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <>
          <input
            ref={ref}
            type="file"
            accept="image/*"
            capture={useFrontCamera ? 'user' : 'environment'}
            onChange={(e) => pick(e.target.files?.[0])}
            className="hidden"
            data-testid={testId}
          />
          <button
            onClick={() => ref.current?.click()}
            disabled={busy}
            className="w-full border-2 border-dashed border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50/50 rounded-xl p-5 text-center text-sm text-emerald-700 font-semibold transition-colors disabled:opacity-50"
          >
            {busy ? (<><Loader2 className="inline-block animate-spin mr-1.5" size={14} /> Processing…</>) : (<><Upload className="inline-block mr-1.5" size={14} /> Tap to upload / take photo</>)}
          </button>
        </>
      )}
    </div>
  );
};

export default TenantOnboard;
